import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { GridController, TSortDirections } from '../../classes/grid-page-info';
import {
  DropDownItem,
  FilterItems,
  type ISelect,
} from '../../components/filters/filter.interface';
import {
  claimFilters,
  IClaimDTO,
} from '../../interfaces/claim/claim.interface';
import { CommonGraphqlService } from '../common.graphql.service';

@Injectable({
  providedIn: 'root',
})
export class ClaimsStateService extends GridController<claimFilters> {
  private _claims: IClaimDTO[];

  private _filter: claimFilters = {
    claimStatus: '',
    claimsDate: '',
    claimId: '',
    patientAccountNumber: '',
    patientName: '',
    reportId: '',
    billingNpi: '',
    renderingNpi: '',
    showLast: '',
    dateFrom: '',
    dateTo: '',
    processorId: '', //RECEIVER
    neicId: '',
    formatType: '', //formattype
    billingTaxId: '',
  };

  private _archiveFilter: claimFilters = {
    claimsDate: '',
    claimId: '',
    patientAccountNumber: '',
    patientName: '',
    showLast: '',
    dateFrom: '',
    dateTo: '',
  };

  claimsArchiveFilters: FilterItems = {
    filtersName: 'Claims',
    items: [
      {
        name: 'claimId',
        caption: 'claim ID',
        type: 'text',
        placeholder: 'Enter Claim ID',
        class: 'claimId',
      },
      {
        name: 'patientAccountNumber',
        caption: 'Patient Account Number',
        type: 'text',
        placeholder: 'Enter Patient Account Number',
        class: 'patientAccountNumber',
      },
      {
        name: 'patientName',
        caption: 'Patient Name',
        type: 'text',
        placeholder: 'Enter Patient Name',
        class: 'patientName',
      },
      {
        caption: 'claims date',
        name: 'claimsDate',
        placeholder: 'Select Claims Date',
        type: 'select',
        values: [
          { value: 'Received Date', key: 'dateOfSubmission' },
          { value: 'Service Date', key: 'firstServiceDate' },
        ],
        class: 'datesType',
      },
      {
        name: 'dateRange',
        type: 'dateRangeCalendarInline',
        dateFromControlName: 'dateFrom',
        dateToControlName: 'dateTo',
        class: 'daterange',
      },
    ],
  };

  claimsFilters: FilterItems = {
    filtersName: 'Claims',
    items: [
      {
        name: 'claimStatus',
        caption: 'Status',
        type: 'ngselect',
        placeholder: 'Select Status',
        bindLabel: 'value',
        bindValue: 'key',
        multiple: true,
        withCheckbox: true,
        values: [
          { key: 'ALL_FINALIZED', value: 'Complete' },
          { key: 'REJECTED', value: 'Rejected' }, // ALL_REJECTED ?
          { key: 'SC_REJECTED', value: 'SC Rejected' },
          { key: 'FINALIZED_DENIED', value: 'Error' },
          { key: 'FINALIZED_PAID', value: 'Paid' },
          { key: 'ACCEPTED_PENDING', value: 'Accepted' }, //
          { key: 'TEST', value: 'Test' },
        ],
        class: 'status',
      },

      {
        name: 'claimId',
        caption: 'claim ID',
        type: 'text',
        placeholder: 'Enter Claim ID',
        class: 'claimId',
      },
      {
        name: 'patientAccountNumber',
        caption: 'Patient Account Number',
        type: 'text',
        placeholder: 'Enter Patient Account Number',
        class: 'patientAccountNumber',
      },
      {
        name: 'patientName',
        caption: 'Patient Name',
        type: 'text',
        placeholder: 'Enter Patient Name',
        class: 'patientName',
      },
      {
        name: 'reportId',
        caption: 'Group Id',
        type: 'text',
        placeholder: 'Enter Group Id',
        class: 'groupId',
      },
      {
        name: 'billingNpi',
        caption: 'Billing npi',
        type: 'text',
        placeholder: 'Enter Billing NPI',
        class: 'billingNpi',
      },
      {
        name: 'renderingNpi',
        caption: 'render npi',
        type: 'text',
        placeholder: 'Enter render npi',
        class: 'renderingNpi',
      },
      {
        name: 'billingTaxId',
        caption: 'Tax ID',
        type: 'text',
        placeholder: 'Enter Tax ID',
        class: 'taxId',
      },
      {
        caption: 'claims date',
        name: 'claimsDate',
        placeholder: 'Select Claims Date',
        type: 'select',
        values: [
          { value: 'Received Date', key: 'dateOfSubmission' },
          { value: 'Service Date', key: 'firstServiceDate' },
        ],
        class: 'datesType',
      },
      {
        name: 'showLast',
        caption: 'show last',
        type: 'showlast',
        placeholder: 'Select Date',
        values: [7, 30, 60, 90],
        class: 'showLast',
      },
      {
        name: 'dateRange',
        type: 'dateRangeCalendarInline',
        dateFromControlName: 'dateFrom',
        dateToControlName: 'dateTo',
        class: 'daterange',
      },
      {
        name: 'processorId',
        caption: 'receiver',
        type: 'text',
        placeholder: 'Enter Receiver',
        class: 'receiver',
      },
      {
        name: 'neicId',
        caption: 'Payer id',
        type: 'text',
        placeholder: 'Enter payer id',
        class: 'payerId',
      },
      {
        name: 'formatType',
        caption: 'format type',
        type: 'select',
        placeholder: 'Select Format',
        // asyncValues: this.getFormatTypes(),
        asyncValues: undefined,
        class: 'formattype',
      },
    ],
  };

  override _sortDirections: TSortDirections = {
    patientName: 'ASC',
    patientAccountNumber: '',
    dateOfService: '',
    claimId: '',
    providerFirstname: '',
    totalCharges: '',
    claimStatus: '',
  };

  constructor(private srvCommmon: CommonGraphqlService) {
    super();
    this.init(this._filter, 1, 'patientName');

    const formatTypeSelect = this.claimsFilters.items?.find((item) => {
      return item.name === 'formatType';
    }) as ISelect;

    formatTypeSelect.asyncValues = this.getFormatTypes();
  }

  get claims(): IClaimDTO[] {
    return this._claims;
  }

  set claims(value: IClaimDTO[]) {
    this._claims = value;
  }

  getFormatTypes(): Observable<DropDownItem[]> {
    return this.srvCommmon.getFormatTypes().pipe(
      map((formatTypes) => {
        return formatTypes.map((ft) => {
          return { key: ft.id, value: ft.description };
        });
      }),
    );
  }
}
